import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ContactForm from "@/pages/about/components/ContactForm";
import { testimonials } from "@/mocks/testimonials";
import { siteInfo, siteStats } from "@/mocks/site";
import { SITE_URL } from "@/utils/site";

const sections = [
  { id: "story", label: "品牌介绍" },
  { id: "position", label: "网站定位" },
  { id: "philosophy", label: "发展理念" },
  { id: "principles", label: "内容原则" },
  { id: "reputation", label: "用户口碑" },
  { id: "contact", label: "联系我们" },
];

const principles = [
  {
    icon: "ri-user-voice-line",
    title: "先聊感受，再谈观点",
    desc: "每个人说话的顺序不同，先听懂对方在意的点，讨论才不容易变成对抗。",
  },
  {
    icon: "ri-scales-3-line",
    title: "事实与情绪分开摆放",
    desc: "热点信息真假难辨，我们鼓励先标明信息源，再表达感受，不把猜测当成结论。",
  },
  {
    icon: "ri-forbid-2-line",
    title: "拒绝人身攻击与引战",
    desc: "可以不同意观点，但不针对个人。恶意引战、地域歧视、辱骂会被版主处理。",
  },
  {
    icon: "ri-file-text-line",
    title: "广告与搬运要说明来意",
    desc: "推广内容需要明确标注，转载他人内容请注明出处，尊重原创也是尊重阅读的人。",
  },
];

export default function About() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于瓜田社区",
      url: `${SITE_URL}/about`,
      inLanguage: "zh-CN",
      description:
        "瓜田社区的品牌介绍、网站定位、发展理念、内容原则与联系方式，一个认真说话的线上唠嗑现场。",
      about: {
        "@type": "Organization",
        name: "瓜田社区",
        url: `${SITE_URL}/`,
        email: siteInfo.email,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "联系瓜田社区",
      url: `${SITE_URL}/about#contact`,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE_URL}/about` },
      ],
    },
  ];

  return (
    <>
      <Seo
        title="关于我们 - 瓜田社区 | 品牌介绍、内容原则与联系方式"
        description="了解瓜田社区的品牌定位、发展理念与内容原则。一个围绕今日话题与讨论版的中文社区，鼓励发帖互动与回帖讨论，让每一次发言都被认真对待。"
        keywords="瓜田社区,关于瓜田社区,瓜田社区内容原则,瓜田社区讨论版,瓜田社区大家聊"
        path="/about"
        jsonLd={jsonLd}
      />

      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1240px] px-4 py-8 md:px-6 md:py-12">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-4 font-brand text-3xl text-foreground-950 md:text-5xl">瓜田社区</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {siteInfo.intro}
          </p>
          <dl className="mt-8 grid grid-cols-2 gap-x-4 gap-y-6 border-t border-background-300 pt-6 md:grid-cols-4">
            {siteStats.map((stat) => (
              <div key={stat.label}>
                <dt className="flex items-center gap-1.5 text-xs text-foreground-500">
                  <i className={stat.icon} />
                  {stat.label}
                </dt>
                <dd className="mt-1.5 text-xl font-semibold text-foreground-950 md:text-2xl">
                  {stat.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <div className="mx-auto max-w-[1240px] px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <nav aria-label="页面目录" className="lg:col-span-3">
            <div className="lg:sticky lg:top-24">
              <h2 className="text-xs font-medium tracking-widest text-foreground-400">目录</h2>
              <ul className="mt-3 space-y-1 border-l border-background-200 pl-3">
                {sections.map((section) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="block rounded-md py-1.5 text-sm text-foreground-600 transition-colors hover:text-primary-700"
                    >
                      {section.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </nav>

          <div className="space-y-12 lg:col-span-9">
            <section id="story" className="scroll-mt-24">
              <span className="text-xs font-medium tracking-widest text-primary-600">STORY</span>
              <h2 className="mt-2 text-2xl font-bold text-foreground-950">品牌介绍</h2>
              <div className="mt-5 grid grid-cols-1 gap-6 md:grid-cols-2 md:items-center">
                <div className="space-y-4 text-sm leading-relaxed text-foreground-700">
                  <p>
                    「瓜田」这个名字来自大家熟悉的那个词——吃瓜。我们想做的，是一个可以安心吃瓜、也能好好说话的地方，而不是又一个吵完就散的信息流。
                  </p>
                  <p>
                    这里没有复杂的规则，也无意做严肃的学术讨论。今天谁遇到一件好笑的事、谁在纠结要不要换工作、谁刚追完一部想吐槽的剧，都可以随手发上来。
                  </p>
                  <p>
                    名字里的「田」还有另一层意思：它代表慢慢长出来的东西。关系、兴趣、信任，都是聊着聊着才长出来的，这也是瓜田最珍惜的部分。
                  </p>
                </div>
                <div className="overflow-hidden rounded-lg">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20illustration%20of%20a%20quiet%20green%20melon%20field%20with%20a%20small%20wooden%20notice%20board%20and%20afternoon%20sunlight%2C%20artistic%20painterly%20style%2C%20gentle%20calm%20mood%2C%20cream%20and%20deep%20green%20palette%2C%20no%20text&width=900&height=700&seq=gt-about-story&orientation=portrait"
                    alt="瓜田社区品牌意象：安静的绿色瓜田插画"
                    title="瓜田社区品牌介绍"
                    className="h-72 w-full object-cover object-top md:h-80"
                  />
                </div>
              </div>
            </section>

            <section id="position" className="scroll-mt-24">
              <span className="text-xs font-medium tracking-widest text-accent-600">POSITION</span>
              <h2 className="mt-2 text-2xl font-bold text-foreground-950">网站定位</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                瓜田社区是一个以话题为核心的中文讨论社区。它不做资讯门户，也不是工具站，而是把「今天大家在聊什么」这件事做得更舒服一点。
              </p>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-lg text-primary-700">
                    <i className="ri-chat-history-line" />
                  </span>
                  <h3 className="mt-3 text-sm font-semibold text-foreground-950">今日话题</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                    每天一个开放议题，降低开口成本，任何人都能接上一句。
                  </p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-lg text-accent-700">
                    <i className="ri-fire-line" />
                  </span>
                  <h3 className="mt-3 text-sm font-semibold text-foreground-950">热帖推荐</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                    按互动量动态排序，让真正被讨论的内容浮上来，而不是靠标题党。
                  </p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-lg text-secondary-800">
                    <i className="ri-community-line" />
                  </span>
                  <h3 className="mt-3 text-sm font-semibold text-foreground-950">板块分区</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                    八个板块各聊各的，追热点的、过日子的、吐槽职场的都能找到主场。
                  </p>
                </div>
              </div>
            </section>

            <section id="philosophy" className="scroll-mt-24">
              <span className="text-xs font-medium tracking-widest text-secondary-700">
                PHILOSOPHY
              </span>
              <h2 className="mt-2 text-2xl font-bold text-foreground-950">发展理念</h2>
              <div className="mt-5 space-y-4 border-l-2 border-primary-300 pl-5 text-sm leading-relaxed text-foreground-700">
                <p>
                  我们相信社区的价值不在规模，而在「愿意继续说话的人有多少」。所以比起追求发帖量，我们更在意每一条帖子下面有没有人认真回应。
                </p>
                <p>
                  我们也相信分歧是正常的。同一个热点，有人关注事实，有人关注情绪，只要不针对个人，差异反而让讨论更有意思。
                </p>
                <p>
                  最后，我们希望瓜田是一个不用「立人设」的地方。你不必是专家，也不必观点完美，只需要愿意把话说清楚一点。
                </p>
              </div>
            </section>

            <section id="principles" className="scroll-mt-24">
              <span className="text-xs font-medium tracking-widest text-primary-600">
                PRINCIPLES
              </span>
              <h2 className="mt-2 text-2xl font-bold text-foreground-950">内容原则</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                这四条是瓜田社区一直在守的底线，也是版主巡版时会参照的判断标准。
              </p>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {principles.map((item) => (
                  <div
                    key={item.title}
                    className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-lg text-primary-700">
                      <i className={item.icon} />
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950">{item.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section id="reputation" className="scroll-mt-24">
              <span className="text-xs font-medium tracking-widest text-accent-600">VOICES</span>
              <h2 className="mt-2 text-2xl font-bold text-foreground-950">用户口碑</h2>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {testimonials.slice(0, 4).map((item) => (
                  <figure
                    key={item.name}
                    className="rounded-lg border border-background-200 bg-background-100/60 p-5"
                  >
                    <blockquote className="text-sm leading-relaxed text-foreground-700">
                      「{item.content}」
                    </blockquote>
                    <figcaption className="mt-4 flex items-center gap-2.5">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-xs font-medium text-background-50">
                        {item.avatar}
                      </span>
                      <span className="text-sm font-medium text-foreground-950">{item.name}</span>
                      <span className="text-xs text-foreground-500">· {item.tag}</span>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </section>

            <section id="contact" className="scroll-mt-24">
              <span className="text-xs font-medium tracking-widest text-primary-600">CONTACT</span>
              <h2 className="mt-2 text-2xl font-bold text-foreground-950">联系我们</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                内容建议、社区合作或问题反馈，都可以通过下面的方式找到我们。日常闲聊直接去讨论版就好，那边回得最快。
              </p>

              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-lg text-primary-700">
                    <i className="ri-mail-line" />
                  </span>
                  <h3 className="mt-3 text-sm font-semibold text-foreground-950">联系邮箱</h3>
                  <p className="mt-1.5 break-all text-sm text-foreground-600">{siteInfo.email}</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-lg text-accent-700">
                    <i className="ri-time-line" />
                  </span>
                  <h3 className="mt-3 text-sm font-semibold text-foreground-950">在线时间</h3>
                  <p className="mt-1.5 text-sm text-foreground-600">{siteInfo.workTime}</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-lg text-secondary-800">
                    <i className="ri-chat-smile-2-line" />
                  </span>
                  <h3 className="mt-3 text-sm font-semibold text-foreground-950">最快的方式</h3>
                  <p className="mt-1.5 text-sm text-foreground-600">在讨论版发一条帖子，通常当天就有回应。</p>
                </div>
              </div>

              <div className="mt-6">
                <ContactForm />
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/topics"
                  className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-quill-pen-line" />
                  去讨论版聊聊
                </Link>
                <Link
                  to="/#faq"
                  className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-question-line" />
                  查看常见问题
                </Link>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}